import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  PLATFORM_ID,
  TemplateRef,
  computed,
  inject,
} from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { toObservable } from '@angular/core/rxjs-interop';
import { FormControl, FormGroup } from '@angular/forms';

import {
  BehaviorSubject,
  combineLatest,
  combineLatestWith,
  filter,
  forkJoin,
  map,
  merge,
  Observable,
  of,
  startWith,
  Subscription,
  switchMap,
  delay,
  distinctUntilChanged,
  take,
  Subject,
} from 'rxjs';

import { SvgLibraryIcon } from '@finnairoyj/fcom-ui-styles';

import { isPresent, doLater, isDeepEqual } from '@fcom/core/utils/utils';
import { LocalDate } from '@fcom/core/utils/tz-date';
import { unsubscribe } from '@fcom/core/utils/unsubscribe';
import { ButtonMode, ButtonSize, ButtonTheme } from '@fcom/ui-components/components/buttons/button/enums';
import { DateLabels, UiLabels } from '@fcom/ui-components/components/datepicker/datepicker/datepicker.component';
import { DateRange } from '@fcom/ui-components/components/datepicker/interfaces';
import { LoaderTheme } from '@fcom/ui-components/components/loader/enums';
import { PopoverOptions } from '@fcom/ui-components/components/popover/popover.interface';
import { PopoverPosition } from '@fcom/ui-components/components/popover/popover.enum';
import { Day, Month } from '@fcom/ui-components/utils/date.interface';
import { Amount } from '@fcom/dapi/interfaces/dapi.interface';
import { finShare } from '@fcom/rx/operators/finShare';
import { LanguageService } from '@fcom/ui-translate/services/language.service';
import { LocationRouteCffService } from '@fcom/core/services/location-routecff/location-routecff.service';
import { TripType } from '@fcom/core/interfaces/triptype.interface';
import { Direction } from '@fcom/common/interfaces/utils.interface';
import { ElementTypes, ElementActions, GaContext, SearchType } from '@fcom/common/interfaces/gtm.interface';
import { FlightSegment } from '@fcom/common/interfaces/flight-search.interface';
import { GlobalBookingTripDates } from '@fcom/common/interfaces/global-booking-trip-dates.interface';
import { PriceCalendarParams, PriceCalendarWithPricesParams } from '@fcom/common/interfaces/price-calendar.interface';
import { BOOKING_WIDGET_CONTEXT } from '@fcom/booking-widget/constants';
import { CmsTripTypeMap } from '@fcom/core/utils/tripType.utils';

import { DatePickerPrices, PriceCalendarCTAParams } from '../../interfaces/price-calendar.interface';
import { HistogramBar } from '../../interfaces/histogram.interface';
import { FinnairTheme } from '../../interfaces/theme.interface';
import { DAYS_MONTH, PriceCalendarService } from '../../services/price-calendar/price-calendar.service';
import { FinnairThemeService } from '../../services/theme/finnair-theme.service';
import { MediaQueryService } from '../../services/media-query/media-query.service';
import {
  calendarDatesToGlobalBookingTripDates,
  priceCalendarBaseParamsAreValid,
  shouldUpdateCalendarDates,
} from '../../utils/price-calendar.utils';
import { GtmService } from '../../gtm/services/gtm.service';

export const PRICE_CALENDAR_SELECTABLE_DAYS = 360;

export enum PriceCalendarType {
  PRICES = 'prices',
  ONLY_DATES = 'onlyDates',
}

@Component({
  selector: 'fin-price-calendar',
  templateUrl: 'price-calendar.component.html',
  styleUrls: ['./price-calendar.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class PriceCalendarComponent implements OnInit, OnDestroy {
  private priceCalendarService = inject(PriceCalendarService);
  private locationRouteCffService = inject(LocationRouteCffService);
  private languageService = inject(LanguageService);
  private gtmService = inject(GtmService);
  private mediaQuerySerivce = inject(MediaQueryService);
  private finnairThemeService = inject(FinnairThemeService);
  private platformId = inject(PLATFORM_ID);

  readonly ctaButtonTheme = computed(() =>
    this.finnairThemeService.siteTheme() === FinnairTheme.LEGACY
      ? ButtonTheme.LIGHT_PRIMARY_NORDIC_BLUE
      : ButtonTheme.PRIMARY_ACCENT
  );

  readonly datepickerLabels$ = this.languageService.translate<UiLabels>('datepicker');
  readonly dateLabels$ = this.languageService.translate<DateLabels>('date');

  readonly ButtonTheme = ButtonTheme;
  readonly ButtonSize = ButtonSize;
  readonly ButtonMode = ButtonMode;
  readonly TripType = TripType;
  readonly SvgLibraryIcon = SvgLibraryIcon;
  readonly LoaderTheme = LoaderTheme;
  readonly CMS_STYLE_OVERRIDE = {
    'margin-top': '0px',
  };
  readonly PriceCalendarType = PriceCalendarType;
  readonly CmsTripTypeMap = CmsTripTypeMap;

  @Input()
  type: PriceCalendarType = PriceCalendarType.PRICES;

  @Input()
  openWithParams$: Observable<PriceCalendarParams | null>;

  @Input()
  showAddReturn = false;

  @Input()
  identifier: string;

  @Input()
  disabledDateRanges$: Observable<DateRange[]> = of([]);

  @Input()
  showFlexibleDatesSelection = false;

  @Input()
  showTripPriceFrom = true;

  @Input()
  showSubtitle = false;

  @Input()
  ctaLabel = '';

  @Input()
  enablePreFlightSearchTracking = true;

  @Input()
  enableNewSearchAutomatically = false;

  @Input()
  isSearchEnabled = false;

  /** Amount of months visible in the calendar grid. Defaults to 2 (booking flow) */
  @Input()
  displayMonths: 1 | 2 = 2;

  /** Renders the calendar embedded in a page instead of inside a modal: non-sticky header and footer */
  @Input()
  embedded = false;

  /** When false, the initial price search is skipped during SSR; the browser still fetches after hydration */
  @Input()
  fetchPricesInSSR = true;

  /** Day content rendered in place of the price when the whole calendar has no prices */
  @Input()
  noPricesDayTemplate: TemplateRef<{ dayValue: Day; dayString: number }>;

  /** Whether prices exist for the initial params, if already known (e.g. checked during SSR). False renders the no-prices state until a live result arrives */
  @Input()
  knownPricesAvailable: boolean | null = null;

  @Output()
  addReturnClicked = new EventEmitter();

  @Output()
  modalClose = new EventEmitter<boolean>();

  @Output()
  ctaClicked = new EventEmitter<PriceCalendarCTAParams>();

  @Output()
  travelDatesChanged = new EventEmitter<GlobalBookingTripDates>();

  @Output()
  startSearch = new EventEmitter<void>();

  @Output()
  cancelSelection = new EventEmitter<void>();

  @Output()
  monthChange = new EventEmitter<Month>();

  @Output()
  visibleRangeChange = new EventEmitter<DateRange>();

  subscription = new Subscription();

  calendarRange: DateRange = [LocalDate.now(), LocalDate.now().plusDays(PRICE_CALENDAR_SELECTABLE_DAYS)];
  pricePerAdult$: Observable<Amount> = of(undefined);
  datesSelected$: Observable<boolean> = of(false);
  datePickerTitleLabel$: Observable<string> = of('');
  subTitle$: Observable<{ origin: string; destination: string }>;
  calendarPrices$: Observable<DatePickerPrices>;
  tripType$: Observable<TripType> = of(undefined);
  travelDates$: Observable<GlobalBookingTripDates> = of(undefined);
  datepickerSelectedDates$: Observable<[LocalDate | undefined, LocalDate | undefined]> = of(undefined);
  flexibleDates$: BehaviorSubject<boolean> = new BehaviorSubject(false);
  flexibleDatesCtrl = new FormControl<boolean>(false, { nonNullable: true });
  flexibleDatesForm = new FormGroup({ flexibleDates: this.flexibleDatesCtrl });
  ctaDisabled$: Observable<boolean> = of(false);
  isLoading$: Observable<boolean> = of(true);
  selectedHistogramMonth$: BehaviorSubject<number> = new BehaviorSubject(undefined);
  scrollToHistogramMonth$: BehaviorSubject<number> = new BehaviorSubject(undefined);
  renderDatepicker$ = new BehaviorSubject(false);
  noPricesAvailableForDates$: Observable<boolean> = of(false);
  pricesAvailable$: Observable<boolean> = of(false);
  showNoPricesDayTemplate$: Observable<boolean> = of(false);
  private readonly isMobile = this.mediaQuerySerivce.isBreakpoint('tablet_down');
  private readonly isMobile$ = toObservable(this.isMobile);
  enablePriceSearchPopover$: Observable<boolean>;
  firstDateToShowPriceSearchPopover$: Observable<LocalDate> = of(undefined);
  preFlightSearchTrigger$ = new Subject<boolean>();

  readonly popoverOptions: PopoverOptions = {
    context: BOOKING_WIDGET_CONTEXT,
    enableOnHover: true,
    zIndex: 10,
    popoverPosition: PopoverPosition.BOTTOM,
    popoverWidth: 180,
    roundedCorners: true,
    showArrowCaret: true,
    useFixedPosition: false,
    showLeftBorder: false,
    hasPadding: true,
    shadow: true,
  };
  hasDiscountCode$: Observable<boolean>;

  ngOnInit(): void {
    this.subscription.add(this.flexibleDatesCtrl.valueChanges.subscribe((v) => this.flexibleDates$.next(v)));
    this.subscription.add(
      this.openWithParams$.pipe(delay(1)).subscribe((params) => {
        this.renderDatepicker$.next(params !== null);
      })
    );
    if (this.fetchPricesInSSR || isPlatformBrowser(this.platformId)) {
      this.startSearchWithSelectedDates();
    }

    this.isLoading$ = this.resolveIsLoading$();
    this.travelDates$ = this.priceCalendarService.travelDates$;
    this.datepickerSelectedDates$ = this.travelDates$.pipe(
      map((dates) => [dates?.departureDate, dates?.returnDate] as [LocalDate | undefined, LocalDate | undefined]),
      distinctUntilChanged(isDeepEqual)
    );

    this.tripType$ = merge(
      this.priceCalendarService.tripType$,
      this.openWithParams$.pipe(
        filter(isPresent),
        map((params) => params.tripType)
      )
    ).pipe(distinctUntilChanged(), finShare());

    if (this.type === PriceCalendarType.PRICES) {
      this.calendarPrices$ = this.priceCalendarService.calendarPrices$;
      this.pricePerAdult$ = this.priceCalendarService.startingFromPrice$;
    }

    const pricesAvailable$ = this.resolvePricesAvailable$();

    const hasRequiredDates$ = combineLatest([
      this.priceCalendarService.travelDates$,
      this.priceCalendarService.tripType$,
    ]).pipe(
      map(([travelDates, tripType]) => {
        return (
          (tripType === TripType.RETURN && isPresent(travelDates.returnDate)) ||
          (tripType === TripType.ONEWAY && isPresent(travelDates.departureDate))
        );
      })
    );

    const hasConsecutiveFollowingMonthPrices$ = combineLatest([
      this.priceCalendarService.followingMonthPrices$,
      this.travelDates$,
      this.tripType$,
    ]).pipe(
      filter(
        ([followingMonthPrices, travelDates, tripType]) =>
          isPresent(followingMonthPrices) && isPresent(travelDates?.departureDate) && tripType === TripType.RETURN
      ),
      map(([followingMonthPrices, { departureDate }]) =>
        Object.entries(followingMonthPrices).every(
          ([date, value]) => date === departureDate.id || (isPresent(value) && isPresent(value.amount))
        )
      )
    );

    this.enablePriceSearchPopover$ = combineLatest([
      this.tripType$,
      pricesAvailable$,
      this.isMobile$,
      this.isLoading$,
      hasConsecutiveFollowingMonthPrices$,
      hasRequiredDates$,
    ]).pipe(
      map(
        ([tripType, pricesAvailable, isMobile, isLoading, hasConsecutiveFollowingMonthPrices, hasRequiredDates]) =>
          this.type === PriceCalendarType.PRICES &&
          tripType === TripType.RETURN &&
          pricesAvailable &&
          !isMobile &&
          !isLoading &&
          hasConsecutiveFollowingMonthPrices &&
          !hasRequiredDates
      )
    );

    this.firstDateToShowPriceSearchPopover$ = this.travelDates$.pipe(
      filter((travelDates) => isPresent(travelDates?.departureDate)),
      map((travelDates) => travelDates.departureDate.plusDays(DAYS_MONTH))
    );

    if (this.showTripPriceFrom) {
      this.pricesAvailable$ = pricesAvailable$;
      this.noPricesAvailableForDates$ = pricesAvailable$.pipe(
        combineLatestWith(this.priceCalendarService.pricesAvailableForSelectedDates$, hasRequiredDates$),
        map(
          ([pricesAvailable, pricesAvailableForSelectedDates, hasRequiredDates]) =>
            !pricesAvailable || (!pricesAvailableForSelectedDates && hasRequiredDates)
        ),
        startWith(false)
      );
    }

    if (this.type === PriceCalendarType.PRICES && isPresent(this.noPricesDayTemplate)) {
      this.showNoPricesDayTemplate$ = pricesAvailable$.pipe(
        map((pricesAvailable) => {
          return !pricesAvailable;
        })
      );
    }

    this.hasDiscountCode$ = this.priceCalendarService.params$.pipe(map((params) => isPresent(params?.discountCode)));

    this.datesSelected$ = combineLatest([this.travelDates$, this.tripType$]).pipe(
      map(
        ([{ departureDate, returnDate }, tripType]) =>
          isPresent(departureDate) && (tripType === TripType.ONEWAY || isPresent(returnDate))
      )
    );

    this.datePickerTitleLabel$ = combineLatest([this.tripType$, this.travelDates$]).pipe(
      map(([tripType, { departureDate }]) => {
        if (tripType === TripType.ONEWAY) {
          return 'bookingWidget.priceCalendar.selectDeparture.oneWay';
        }
        return isPresent(departureDate)
          ? 'bookingWidget.priceCalendar.selectReturn'
          : 'bookingWidget.priceCalendar.selectDeparture.roundTrip';
      })
    );

    this.ctaDisabled$ = combineLatest([this.tripType$, this.travelDates$]).pipe(
      map(([tripType, travelDates]) => {
        if (tripType === TripType.RETURN) {
          return !isPresent(travelDates.departureDate) || !isPresent(travelDates.returnDate);
        }
        return !isPresent(travelDates.departureDate);
      })
    );

    if (this.showSubtitle) {
      this.subTitle$ = this.priceCalendarService.params$.pipe(
        filter(Boolean),
        switchMap(({ origin, destination }) => {
          return forkJoin({
            origin: this.locationRouteCffService.bestGuessFor(origin, this.languageService.localeValue),
            destination: this.locationRouteCffService.bestGuessFor(destination, this.languageService.localeValue),
          });
        }),
        map(({ origin, destination }) => {
          const originCity = origin ? origin.cityName : '';
          const destinationCity = destination ? destination.cityName : '';
          return { origin: originCity, destination: destinationCity };
        })
      );
    }

    if (this.enablePreFlightSearchTracking) {
      this.subscription.add(
        combineLatest([
          this.preFlightSearchTrigger$,
          this.datesSelected$.pipe(
            distinctUntilChanged(),
            filter((datesSelected) => datesSelected)
          ),
        ])
          .pipe(switchMap(() => this.priceCalendarService.getPriceCalendarCTAParams()))
          .subscribe((ctaParams) => {
            const { tripType, paxAmount, travelClass, flights } = ctaParams;

            const departureFlight: FlightSegment = {
              origin: flights[0]?.origin?.locationCode,
              destination: flights[0]?.destination?.locationCode,
              departureDate: flights[0]?.departureDate,
            };

            const returnFlight: FlightSegment = {
              origin: flights[1]?.origin?.locationCode,
              destination: flights[1]?.destination?.locationCode,
              departureDate: flights[1]?.departureDate,
            };

            const flightSegments: FlightSegment[] =
              tripType === TripType.RETURN ? [departureFlight, returnFlight] : [departureFlight];
            if (paxAmount && travelClass) {
              this.gtmService.preFlightSearch({
                flights: flightSegments,
                paxAmount,
                travelClass,
                isAward: this.type === PriceCalendarType.ONLY_DATES,
              });
            }
          })
      );
    }
  }

  ngOnDestroy(): void {
    unsubscribe(this.subscription);
  }

  closeModal(): void {
    this.modalClose.emit(false);
  }

  addReturn(): void {
    this.priceCalendarService.convertToReturnTrip();
    this.addReturnClicked.emit();
  }

  updateCalendarDates(calendarDates: [string] | [string, string]): void {
    doLater(() => {
      const newDates = calendarDatesToGlobalBookingTripDates(calendarDates);
      if (shouldUpdateCalendarDates(newDates, this.priceCalendarService.travelDatesValues)) {
        this.priceCalendarService.updateCalendarDates(newDates);
        this.travelDatesChanged.emit(newDates);
        this.preFlightSearchTrigger$.next(true);
      }
    });
  }

  onMonthChange(month: Month): void {
    this.setHistogramMonth(month?.monthArrayIndex);
    this.monthChange.emit(month);
  }

  setHistogramMonth(selectedMonthIndex = 0): void {
    this.selectedHistogramMonth$.next(selectedMonthIndex);
  }

  handleHistogramClick(selectedBar: HistogramBar): void {
    if (!selectedBar.noFlight) {
      this.gtmService.trackElement(
        'histogram',
        'flight-search',
        ElementTypes.BOOKING_WIDGET,
        selectedBar?.isCheapest ? 'lowest-month-click' : 'other-month-click',
        ElementActions.CLICK,
        undefined,
        this.type === PriceCalendarType.ONLY_DATES ? SearchType.AWARD : SearchType.BOOKING_FLOW
      );
    }
    this.scrollToHistogramMonth$.next(selectedBar.index);
  }

  handleBarsScrolled(_direction: Direction): void {
    //TODO: Check what this doing ?
    this.gtmService.trackElement(
      'histogram',
      'flight-search',
      ElementTypes.BOOKING_WIDGET,
      'arrow-click',
      ElementActions.CLICK,
      undefined,
      this.type === PriceCalendarType.ONLY_DATES ? SearchType.AWARD : SearchType.BOOKING_FLOW
    );
  }

  emitCTAClicked(): void {
    doLater(() => {
      this.subscription.add(
        this.priceCalendarService
          .getPriceCalendarCTAParams()
          .pipe(take(1))
          .subscribe((ctaParams) => {
            this.ctaClicked.emit({
              ...ctaParams,
              flexibleDates: this.flexibleDates$.value,
            });
          })
      );
    });
  }

  onCancelSelection(): void {
    this.cancelSelection.emit();
    this.startSearchWithSelectedDates(true);
    this.closeModal();
  }

  onClose(): void {
    if (this.enableNewSearchAutomatically) {
      this.onCancelSelection();

      this.gtmService.trackElement(
        'global-widget-cancel-new-search-from-price-calendar',
        GaContext.BOOKING_FLOW,
        ElementTypes.BOOKING_WIDGET,
        ElementTypes.BUTTON,
        '',
        ElementActions.CLICK
      );
    }
  }

  startNewSearch(): void {
    this.startSearch.emit();
    this.modalClose.next(true);

    this.gtmService.trackElement(
      'global-widget-start-new-search-from-price-calendar',
      GaContext.BOOKING_FLOW,
      ElementTypes.BOOKING_WIDGET,
      ElementTypes.BUTTON,
      '',
      ElementActions.CLICK
    );
  }

  startSearchWithSelectedDates(onlyOnce = false): void {
    this.subscription.add(
      this.openWithParams$
        .pipe(
          onlyOnce ? take(1) : map((params) => params),
          filter((params) => isPresent(params) && priceCalendarBaseParamsAreValid(params)),
          distinctUntilChanged(
            (pre: PriceCalendarWithPricesParams, next: PriceCalendarWithPricesParams) =>
              pre.origin === next.origin &&
              pre.destination === next.destination &&
              pre.tripType === next.tripType &&
              pre.travelClass === next.travelClass
          )
        )
        .subscribe((params) => {
          this.selectedHistogramMonth$.next(undefined);
          this.priceCalendarService.updateParams({
            ...params,
            initialTravelDates: {
              ...params.initialTravelDates,
              returnDate: params.tripType !== TripType.RETURN ? undefined : params.initialTravelDates?.returnDate,
            },
          });
        })
    );
  }

  private resolvePricesAvailable$(): Observable<boolean> {
    if (this.knownPricesAvailable !== false) {
      return this.priceCalendarService.pricesAvailable$;
    }
    if (!isPlatformBrowser(this.platformId)) {
      return of(false);
    }
    return this.priceCalendarService.pricesAvailable$.pipe(startWith(false));
  }

  private resolveIsLoading$(): Observable<boolean> {
    if (this.type !== PriceCalendarType.PRICES) {
      return of(false);
    }
    if (this.knownPricesAvailable !== null && !isPlatformBrowser(this.platformId)) {
      return of(this.knownPricesAvailable);
    }
    if (this.knownPricesAvailable !== false) {
      return this.priceCalendarService.isLoading$;
    }
    return this.priceCalendarService.pricesAvailable$.pipe(
      take(1),
      switchMap(() => {
        return this.priceCalendarService.isLoading$;
      }),
      startWith(false)
    );
  }
}
